<template>
    <div class="page-container">
        <soft-alert v-if="alert.state" :color="alert.color" class="mb-3" dismissible>
            {{ alert.message }}
        </soft-alert>

        <CrmPageHeader title="Reputación de inmuebles" :subtitle="periodoTexto" icon="fas fa-star-half-alt"
            iconBg="linear-gradient(135deg,#B45309,#F59E0B)">
            <template v-if="!sinAcceso">
                <button type="button" class="icon-btn" title="Actualizar" :disabled="loadingResumen" @click="cargarReporte">
                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': loadingResumen }"></i>
                </button>
                <soft-button variant="gradient" size="sm" @click="exportar" :loading="exportando"
                    title="Descarga un CSV con los filtros y el orden de la tabla de inmuebles">
                    <i class="fas fa-file-export me-1"></i> Exportar CSV
                </soft-button>
            </template>
        </CrmPageHeader>

        <!-- Sin permisos -->
        <div v-if="sinAcceso" class="crm-card">
            <div class="empty-state">
                <div class="empty-state__icon"><i class="fas fa-lock"></i></div>
                <p class="empty-state__title">No tienes acceso a este reporte</p>
                <p class="empty-state__text">Solo el administrador de la inmobiliaria puede ver el reporte de reputación.</p>
            </div>
        </div>

        <template v-else>
            <!-- Barra de filtros -->
            <div class="crm-card filtros-card mb-4">
                <div class="filtros-bar">
                    <div class="segmented" role="group" aria-label="Periodo">
                        <button v-for="r in rangos" :key="r.key" type="button" class="segmented__btn"
                            :class="{ 'is-active': rangoActivo === r.key }" @click="aplicarRango(r.key)">
                            {{ r.label }}
                        </button>
                    </div>

                    <div class="fechas" :class="{ 'is-active': rangoActivo === 'custom' }">
                        <i class="far fa-calendar-alt"></i>
                        <input type="date" v-model="filtros.fecha_desde" :max="filtros.fecha_hasta || undefined"
                            @change="cambiarFecha" aria-label="Desde" />
                        <span>—</span>
                        <input type="date" v-model="filtros.fecha_hasta" :min="filtros.fecha_desde || undefined"
                            @change="cambiarFecha" aria-label="Hasta" />
                    </div>

                    <div class="filtros-bar__right">
                        <v-select v-if="esPlataforma" :modelValue="filtros.inmobiliaria_id"
                            @update:modelValue="(v) => cambiarFiltro('inmobiliaria_id', v)"
                            :options="opcionesInmobiliarias" :reduce="(o) => o.value"
                            placeholder="Toda la plataforma" class="rep-select rep-select--inmobiliaria" />

                        <button type="button" class="filtros-toggle" :class="{ 'is-open': masFiltros }"
                            @click="masFiltros = !masFiltros">
                            <i class="fas fa-sliders-h me-1"></i> Filtros
                            <span v-if="filtrosSecundarios" class="filtros-toggle__count">{{ filtrosSecundarios }}</span>
                            <i class="fas fa-chevron-down ms-1 filtros-toggle__chevron"></i>
                        </button>
                    </div>
                </div>

                <div v-show="masFiltros" class="filtros-panel">
                    <div class="row g-3">
                        <div class="col-lg-3 col-md-6">
                            <label class="filter-label">Agente</label>
                            <v-select :modelValue="filtros.agent_id" @update:modelValue="(v) => cambiarFiltro('agent_id', v)"
                                :options="opcionesAgentes" :reduce="(o) => o.value" :disabled="!opcionesAgentes.length"
                                :placeholder="opcionesAgentes.length ? 'Todos los agentes' : 'Elige primero una inmobiliaria'"
                                class="rep-select" />
                        </div>
                        <div class="col-lg-3 col-md-6">
                            <label class="filter-label">Ciudad</label>
                            <v-select :modelValue="filtros.ciudad_id" @update:modelValue="(v) => cambiarFiltro('ciudad_id', v)"
                                :options="opcionesCiudades" :reduce="(o) => o.value" placeholder="Todas las ciudades"
                                class="rep-select" />
                        </div>
                        <div class="col-lg-3 col-md-6">
                            <label class="filter-label">Tipo de inmueble</label>
                            <v-select :modelValue="filtros.tipo_inmueble" @update:modelValue="(v) => cambiarFiltro('tipo_inmueble', v)"
                                :options="opcionesTiposInmueble" :reduce="(o) => o.value" placeholder="Todos los tipos"
                                class="rep-select" />
                        </div>
                        <div class="col-lg-3 col-md-6">
                            <label class="filter-label">Tipo de negocio</label>
                            <v-select :modelValue="filtros.tipo_negocio" @update:modelValue="(v) => cambiarFiltro('tipo_negocio', v)"
                                :options="opcionesTiposNegocio" :reduce="(o) => o.value" placeholder="Venta y arriendo"
                                class="rep-select" />
                        </div>
                    </div>
                </div>

                <div v-if="filtrosActivos.length || hayFiltrosModificados" class="chips-activos">
                    <span v-for="chip in filtrosActivos" :key="chip.campo" class="chip-filtro">
                        <span class="chip-filtro__label">{{ chip.label }}:</span> {{ chip.valor }}
                        <button type="button" @click="quitarFiltro(chip.campo)" :aria-label="`Quitar ${chip.label}`">
                            <i class="fas fa-times"></i>
                        </button>
                    </span>
                    <button v-if="hayFiltrosModificados" type="button" class="link-limpiar" @click="limpiarFiltros">
                        <i class="fas fa-undo-alt me-1"></i> Restablecer
                    </button>
                </div>
            </div>

            <!-- Barra de progreso al refrescar -->
            <div class="progress-line" :class="{ 'is-active': refrescando }"></div>

            <!-- Esqueleto de la primera carga -->
            <template v-if="cargandoPrimeraVez">
                <div class="row g-3 mb-4">
                    <div v-for="n in 4" :key="n" class="col-xl-3 col-sm-6">
                        <div class="stat-card"><div class="skeleton skeleton--kpi"></div></div>
                    </div>
                </div>
                <div class="row g-3 mb-4">
                    <div class="col-xl-8"><div class="crm-card p-3"><div class="skeleton skeleton--chart"></div></div></div>
                    <div class="col-xl-4"><div class="crm-card p-3"><div class="skeleton skeleton--chart"></div></div></div>
                </div>
            </template>

            <!-- Sin calificaciones en el periodo -->
            <div v-else-if="sinCalificaciones" class="crm-card mb-4" :class="{ 'is-refreshing': refrescando }">
                <div class="empty-state">
                    <div class="empty-state__icon"><i class="far fa-star"></i></div>
                    <p class="empty-state__title">No hay calificaciones en este periodo</p>
                    <p class="empty-state__text">
                        <template v-if="kpis.inmuebles_total">
                            Hay {{ formatNumber(kpis.inmuebles_total) }} inmuebles con estos filtros y ninguno fue calificado
                            {{ rangoActivo === 'todo' ? 'todavía' : 'en estas fechas' }}.
                        </template>
                        <template v-else>Prueba con otro rango de fechas o quita algunos filtros.</template>
                    </p>
                    <div class="d-flex flex-wrap justify-content-center gap-2">
                        <soft-button v-if="rangoActivo !== 'todo'" variant="gradient" size="sm" @click="aplicarRango('todo')">
                            <i class="fas fa-history me-1"></i> Ver todo el histórico
                        </soft-button>
                        <soft-button v-if="filtrosActivos.length" variant="outline" color="secondary" size="sm" @click="limpiarFiltros">
                            Quitar filtros
                        </soft-button>
                    </div>
                </div>
            </div>

            <div v-else-if="resumen" :class="{ 'is-refreshing': refrescando }">
                <!-- KPIs -->
                <div class="row g-3 mb-4">
                    <div class="col-xl-3 col-sm-6">
                        <div class="stat-card kpi h-100">
                            <div class="kpi__head">
                                <span class="stat-card__label">Promedio general</span>
                                <i class="fas fa-info-circle kpi__info"
                                    title="60 % criterios del inmueble + 40 % criterios del agente, de 1 a 5."></i>
                            </div>
                            <div class="d-flex align-items-end gap-2">
                                <span class="kpi__value">{{ formatPromedio(kpis.promedio_general) }}</span>
                                <span class="kpi__of">/ 5</span>
                                <span class="nivel-chip ms-auto" :style="nivelStyle(kpis.nivel)">{{ nivelNombre(kpis.nivel) }}</span>
                            </div>
                            <estrellas :valor="kpis.promedio_general" class="mt-1" />
                            <div class="kpi__split">
                                <div><span>Inmueble</span><b>{{ formatPromedio(kpis.promedio_inmueble) }}</b></div>
                                <div><span>Agente</span><b>{{ formatPromedio(kpis.promedio_agente) }}</b></div>
                            </div>
                            <div class="kpi__deltas">
                                <span v-if="anterior" class="delta" :class="variacionClass(anterior.variacion?.promedio_general)"
                                    :title="`Periodo anterior (${formatFecha(anterior.desde)} – ${formatFecha(anterior.hasta)}): ${formatPromedio(anterior.promedio_general)}`">
                                    <i :class="variacionIcon(anterior.variacion?.promedio_general)"></i>
                                    {{ formatVariacion(anterior.variacion?.promedio_general) }}
                                    <small>vs anterior</small>
                                </span>
                                <span v-if="resumen.plataforma" class="delta" :class="variacionClass(resumen.plataforma.diferencia?.promedio_general)"
                                    :title="`Promedio de toda la plataforma: ${formatPromedio(resumen.plataforma.promedio_general)}`">
                                    <i :class="variacionIcon(resumen.plataforma.diferencia?.promedio_general)"></i>
                                    {{ formatVariacion(resumen.plataforma.diferencia?.promedio_general) }}
                                    <small>vs plataforma</small>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="col-xl-3 col-sm-6">
                        <div class="stat-card kpi h-100">
                            <div class="kpi__head">
                                <span class="stat-card__label">Calificaciones</span>
                                <i class="fas fa-info-circle kpi__info"
                                    title="Calificaciones recibidas en el periodo. Si alguien edita su calificación, cuenta en la fecha del cambio."></i>
                            </div>
                            <span class="kpi__value">{{ formatNumber(kpis.total_calificaciones) }}</span>
                            <p class="kpi__hint">
                                De {{ formatNumber(kpis.calificadores) }} {{ kpis.calificadores === 1 ? 'usuario' : 'usuarios' }}
                                de otras inmobiliarias
                            </p>
                            <div class="kpi__deltas">
                                <span v-if="anterior" class="delta" :class="variacionClass(anterior.variacion?.total_calificaciones)"
                                    :title="`Periodo anterior: ${formatNumber(anterior.total_calificaciones)} calificaciones`">
                                    <i :class="variacionIcon(anterior.variacion?.total_calificaciones)"></i>
                                    {{ formatVariacion(anterior.variacion?.total_calificaciones, '%') }}
                                    <small>vs anterior</small>
                                </span>
                            </div>
                            <p v-if="kpis.ultima_calificacion" class="kpi__hint mt-auto pt-2">
                                <i class="far fa-clock me-1"></i>Última: {{ formatFechaHora(kpis.ultima_calificacion) }}
                            </p>
                        </div>
                    </div>

                    <div class="col-xl-3 col-sm-6">
                        <div class="stat-card kpi h-100">
                            <div class="kpi__head">
                                <span class="stat-card__label">Percepción</span>
                                <i class="fas fa-info-circle kpi__info"
                                    title="Positiva: promedio ≥ 4 · Neutra: entre 3 y 3.9 · Negativa: menor a 3."></i>
                            </div>
                            <div class="d-flex align-items-end gap-2">
                                <span class="kpi__value">{{ formatPorcentaje(kpis.porcentaje_positivas) }}</span>
                                <span class="kpi__of">positivas</span>
                            </div>
                            <div class="sentimiento-bar">
                                <div class="sentimiento-bar__pos" :style="{ width: (kpis.porcentaje_positivas || 0) + '%' }"></div>
                                <div class="sentimiento-bar__neu" :style="{ width: (kpis.porcentaje_neutras || 0) + '%' }"></div>
                                <div class="sentimiento-bar__neg" :style="{ width: (kpis.porcentaje_negativas || 0) + '%' }"></div>
                            </div>
                            <div class="leyenda">
                                <span><i class="dot dot--pos"></i>{{ formatNumber(kpis.positivas) }} positivas</span>
                                <span><i class="dot dot--neu"></i>{{ formatNumber(kpis.neutras) }} neutras</span>
                                <span><i class="dot dot--neg"></i>{{ formatNumber(kpis.negativas) }} negativas</span>
                            </div>
                        </div>
                    </div>

                    <div class="col-xl-3 col-sm-6">
                        <div class="stat-card kpi h-100">
                            <div class="kpi__head">
                                <span class="stat-card__label">Cobertura</span>
                                <i class="fas fa-info-circle kpi__info"
                                    title="Porcentaje de los inmuebles (con los filtros de agente, ciudad y tipo) que recibió al menos una calificación en el periodo."></i>
                            </div>
                            <div class="d-flex align-items-end gap-2">
                                <span class="kpi__value">{{ formatPorcentaje(kpis.cobertura) }}</span>
                                <span class="kpi__of">calificados</span>
                            </div>
                            <div class="bar mt-2">
                                <div class="bar__fill" style="background:#2563EB" :style="{ width: (kpis.cobertura || 0) + '%' }"></div>
                            </div>
                            <p class="kpi__hint mt-2">
                                {{ formatNumber(kpis.inmuebles_calificados) }} de {{ formatNumber(kpis.inmuebles_total) }} inmuebles
                            </p>
                            <a v-if="kpis.inmuebles_sin_calificar" href="#" class="kpi__link mt-auto"
                                @click.prevent="verSinCalificar">
                                Ver {{ formatNumber(kpis.inmuebles_sin_calificar) }} sin calificar <i class="fas fa-arrow-right ms-1"></i>
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Tendencia + criterios -->
                <div class="row g-3 mb-4">
                    <div class="col-xl-8">
                        <div class="crm-card h-100">
                            <div class="card-head">
                                <div>
                                    <p class="card-title-sm">Evolución</p>
                                    <p class="card-hint">
                                        <i class="legend-swatch legend-swatch--bar"></i>Calificaciones
                                        <i class="legend-swatch legend-swatch--line ms-2"></i>Promedio general
                                    </p>
                                </div>
                                <div class="segmented segmented--sm" role="group" aria-label="Agrupación">
                                    <button v-for="a in agrupaciones" :key="a.key" type="button" class="segmented__btn"
                                        :class="{ 'is-active': agrupacion === a.key }"
                                        :disabled="!agrupacionesDisponibles[a.key]"
                                        :title="agrupacionesDisponibles[a.key] ? '' : 'El rango es demasiado largo para esta agrupación'"
                                        @click="cambiarAgrupacion(a.key)">
                                        {{ a.label }}
                                    </button>
                                </div>
                            </div>
                            <div class="card-content" :class="{ 'is-refreshing': loadingTendencia && tendencia }">
                                <div v-if="loadingTendencia && !tendencia" class="skeleton skeleton--chart"></div>
                                <p v-else-if="!tendenciaTieneDatos" class="empty-text">Sin calificaciones en el rango</p>
                                <reputacion-chart v-else type="bar" :data="tendenciaData" :options="tendenciaOptions" :height="280" />
                            </div>
                        </div>
                    </div>

                    <div class="col-xl-4">
                        <div class="crm-card h-100">
                            <div class="card-head">
                                <div>
                                    <p class="card-title-sm">Criterios</p>
                                    <p class="card-hint">Promedio de cada criterio</p>
                                </div>
                            </div>
                            <div class="card-content">
                                <div v-for="c in resumen.criterios" :key="c.clave" class="crit-row"
                                    :class="{ 'crit-row--top': resumen.fortaleza?.clave === c.clave, 'crit-row--low': resumen.a_mejorar?.clave === c.clave }">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <span class="crit-row__name">
                                            <i :class="c.grupo === 'agente' ? 'fas fa-user-tie' : 'fas fa-home'" class="crit-row__icon"
                                                :title="c.grupo === 'agente' ? 'Criterio del agente' : 'Criterio del inmueble'"></i>
                                            {{ c.nombre }}
                                        </span>
                                        <span class="crit-row__val">{{ formatPromedio(c.promedio) }}</span>
                                    </div>
                                    <div class="bar">
                                        <div class="bar__fill" :style="{ width: (c.porcentaje || 0) + '%', background: colorPorPromedio(c.promedio) }"></div>
                                    </div>
                                    <span v-if="resumen.fortaleza?.clave === c.clave" class="crit-tag crit-tag--ok">
                                        <i class="fas fa-trophy me-1"></i>Fortaleza
                                    </span>
                                    <span v-else-if="resumen.a_mejorar?.clave === c.clave" class="crit-tag crit-tag--warn">
                                        <i class="fas fa-exclamation-triangle me-1"></i>A mejorar
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Distribución + niveles + destacados -->
                <div class="row g-3 mb-4">
                    <div class="col-xl-4 col-md-6">
                        <div class="crm-card h-100">
                            <div class="card-head">
                                <div>
                                    <p class="card-title-sm">Distribución de estrellas</p>
                                    <p class="card-hint">Cada calificación según su promedio redondeado</p>
                                </div>
                            </div>
                            <div class="card-content">
                                <div v-for="d in resumen.distribucion" :key="d.estrellas" class="dist-row"
                                    :title="`${formatNumber(d.total)} calificaciones de ${d.estrellas} estrellas`">
                                    <span class="dist-row__label">{{ d.estrellas }} <i class="fas fa-star"></i></span>
                                    <div class="bar flex-grow-1">
                                        <div class="bar__fill" :style="{ width: (d.porcentaje || 0) + '%', background: colorPorPromedio(d.estrellas) }"></div>
                                    </div>
                                    <span class="dist-row__pct">{{ formatPorcentaje(d.porcentaje) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-xl-4 col-md-6">
                        <div class="crm-card h-100">
                            <div class="card-head">
                                <div>
                                    <p class="card-title-sm">Inmuebles por nivel</p>
                                    <p class="card-hint">Según su promedio en el periodo</p>
                                </div>
                            </div>
                            <div class="card-content">
                                <div class="dona">
                                    <reputacion-chart type="doughnut" :data="nivelesData" :options="nivelesOptions" :height="170" />
                                    <div class="dona__centro">
                                        <b>{{ formatNumber(kpis.inmuebles_calificados) }}</b>
                                        <span>inmuebles</span>
                                    </div>
                                </div>
                                <div class="niveles-leyenda">
                                    <div v-for="n in resumen.niveles" :key="n.clave" class="niveles-leyenda__item">
                                        <i class="dot" :style="{ background: nivelSolid(n.clave) }"></i>
                                        <span class="flex-grow-1">{{ n.nombre }}</span>
                                        <b>{{ formatNumber(n.inmuebles) }}</b>
                                        <span class="niveles-leyenda__pct">{{ formatPorcentaje(n.porcentaje) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-xl-4">
                        <div class="crm-card h-100">
                            <div class="card-head">
                                <div>
                                    <p class="card-title-sm">Destacados</p>
                                    <p class="card-hint">Top 5 · clic para ver el detalle</p>
                                </div>
                                <div class="segmented segmented--sm" role="group">
                                    <button type="button" class="segmented__btn" :class="{ 'is-active': destacadosVista === 'mejores' }"
                                        @click="destacadosVista = 'mejores'"><i class="fas fa-arrow-up me-1"></i>Mejores</button>
                                    <button type="button" class="segmented__btn" :class="{ 'is-active': destacadosVista === 'peores' }"
                                        @click="destacadosVista = 'peores'"><i class="fas fa-arrow-down me-1"></i>Peores</button>
                                </div>
                            </div>
                            <div class="card-content">
                                <p v-if="!destacados.length" class="empty-text">Sin datos</p>
                                <a v-for="(item, i) in destacados" :key="item.id" href="#" class="destacado"
                                    @click.prevent="abrirDetalle(item.id)">
                                    <span class="rank" :class="`rank--${destacadosVista}`">{{ i + 1 }}</span>
                                    <div class="destacado__body">
                                        <p class="destacado__titulo">{{ item.titulo || `Inmueble #${item.id}` }}</p>
                                        <p class="destacado__sub">
                                            {{ item.codigo_ficha || `#${item.id}` }}
                                            <template v-if="item.ciudad"> · {{ item.ciudad.nombre }}</template>
                                            · {{ formatNumber(item.reputacion?.total_calificaciones) }} calif.
                                        </p>
                                    </div>
                                    <span class="nivel-chip" :style="nivelStyle(item.reputacion?.nivel)">
                                        <i class="fas fa-star me-1"></i>{{ formatPromedio(item.reputacion?.promedio_general) }}
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Rankings -->
            <div class="crm-card">
                <div class="card-head card-head--tabla">
                    <div class="tabs">
                        <button type="button" class="tabs__btn" :class="{ 'is-active': tab === 'inmuebles' }"
                            @click="cambiarTab('inmuebles')">
                            <i class="fas fa-home me-1"></i> Inmuebles
                        </button>
                        <button type="button" class="tabs__btn" :class="{ 'is-active': tab === 'agentes' }"
                            @click="cambiarTab('agentes')">
                            <i class="fas fa-user-tie me-1"></i> Agentes
                        </button>
                    </div>
                    <span class="total-badge">
                        {{ formatNumber(paginacion.total) }} {{ tab === 'agentes' ? 'agentes' : 'inmuebles' }}
                    </span>
                </div>

                <!-- Controles de la tabla -->
                <div class="tabla-controles">
                    <div v-if="tab === 'inmuebles'" class="buscador">
                        <i class="fas fa-search"></i>
                        <input type="text" v-model="tablaInmuebles.q" placeholder="Buscar por título, código, dirección o ID" />
                        <button v-if="tablaInmuebles.q" type="button" @click="tablaInmuebles.q = ''" aria-label="Limpiar búsqueda">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                    <select class="form-select form-select-sm control" v-model="tablaActual.estado" @change="buscarTabla"
                        aria-label="Estado">
                        <option value="calificados">Solo calificados</option>
                        <option value="sin_calificar">Sin calificar</option>
                        <option value="todos">Todos</option>
                    </select>
                    <select class="form-select form-select-sm control" v-model="tablaActual.orden" @change="buscarTabla"
                        aria-label="Ordenar por">
                        <option v-for="o in ordenes" :key="o.key" :value="o.key">{{ o.label }}</option>
                    </select>
                    <button type="button" class="filtros-toggle filtros-toggle--sm" :class="{ 'is-open': masTabla }"
                        @click="masTabla = !masTabla">
                        <i class="fas fa-sliders-h me-1"></i> Más
                        <span v-if="filtrosTablaActivos" class="filtros-toggle__count">{{ filtrosTablaActivos }}</span>
                    </button>
                </div>
                <div v-show="masTabla" class="tabla-controles tabla-controles--extra">
                    <label class="mini-field">
                        <span>Mín. calificaciones</span>
                        <input type="number" min="1" class="form-control form-control-sm" v-model="tablaActual.min_calificaciones"
                            placeholder="—" @change="buscarTabla" />
                    </label>
                    <template v-if="tab === 'inmuebles'">
                        <label class="mini-field">
                            <span>Promedio desde</span>
                            <input type="number" min="0" max="5" step="0.1" class="form-control form-control-sm"
                                v-model="tablaInmuebles.promedio_min" placeholder="0" @change="buscarTabla" />
                        </label>
                        <label class="mini-field">
                            <span>Promedio hasta</span>
                            <input type="number" min="0" max="5" step="0.1" class="form-control form-control-sm"
                                v-model="tablaInmuebles.promedio_max" placeholder="5" @change="buscarTabla" />
                        </label>
                    </template>
                </div>

                <div class="position-relative">
                    <div class="progress-line progress-line--tabla" :class="{ 'is-active': loadingTabla }"></div>

                    <!-- Tabla inmuebles -->
                    <div v-if="tab === 'inmuebles'" class="table-responsive" :class="{ 'is-refreshing': loadingTabla }">
                        <table class="table align-items-center mb-0 rep-tabla">
                            <thead>
                                <tr>
                                    <th class="col-rank">#</th>
                                    <th>Inmueble</th>
                                    <th v-if="vistaGlobal">Inmobiliaria</th>
                                    <th>Agente</th>
                                    <th>Calificación</th>
                                    <th class="text-center">Calif.</th>
                                    <th title="Fotos · Información · Disponibilidad · Respuesta · Ética">Criterios</th>
                                    <th>Última</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="!inmuebles.items.length && !loadingTabla">
                                    <td :colspan="vistaGlobal ? 9 : 8">
                                        <div class="empty-state empty-state--sm">
                                            <div class="empty-state__icon"><i class="fas fa-search"></i></div>
                                            <p class="empty-state__title">No hay inmuebles con estos filtros</p>
                                            <p class="empty-state__text">Cambia el estado a "Todos" o ajusta la búsqueda.</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-for="(item, i) in inmuebles.items" :key="item.id" class="fila-click" tabindex="0"
                                    @click="abrirDetalle(item.id)" @keyup.enter="abrirDetalle(item.id)">
                                    <td class="col-rank">{{ posicion(i) }}</td>
                                    <td>
                                        <p class="celda-titulo" :title="item.titulo">{{ item.titulo || `Inmueble #${item.id}` }}</p>
                                        <p class="celda-sub">
                                            <span class="codigo">{{ item.codigo_ficha || `#${item.id}` }}</span>
                                            <template v-if="item.ciudad"> · {{ item.ciudad.nombre }}</template>
                                            <template v-if="item.tipo_inmueble"> · {{ item.tipo_inmueble.nombre }}</template>
                                        </p>
                                    </td>
                                    <td v-if="vistaGlobal">
                                        <p class="celda-titulo">{{ item.inmobiliaria?.nombre || '—' }}</p>
                                        <p class="celda-sub">{{ item.inmobiliaria?.email }}</p>
                                    </td>
                                    <td>
                                        <div class="agente-celda">
                                            <span class="avatar">{{ iniciales(item.agente?.nombre) }}</span>
                                            <div>
                                                <p class="celda-titulo">{{ item.agente?.nombre || 'Sin agente' }}</p>
                                                <p class="celda-sub">{{ item.agente?.email }}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div v-if="item.reputacion?.promedio_general !== null && item.reputacion?.promedio_general !== undefined"
                                            class="calif-celda">
                                            <span class="calif-celda__valor">{{ formatPromedio(item.reputacion.promedio_general) }}</span>
                                            <div>
                                                <estrellas :valor="item.reputacion.promedio_general" />
                                                <span class="nivel-chip nivel-chip--xs" :style="nivelStyle(item.reputacion.nivel)">
                                                    {{ nivelNombre(item.reputacion.nivel) }}
                                                </span>
                                            </div>
                                        </div>
                                        <span v-else class="nivel-chip nivel-chip--xs" :style="nivelStyle(null)">Sin calificar</span>
                                    </td>
                                    <td class="text-center fw-semibold">{{ formatNumber(item.reputacion?.total_calificaciones) }}</td>
                                    <td>
                                        <mini-criterios :criterios="item.reputacion?.criterios" :nombres="nombresCriterios" />
                                    </td>
                                    <td class="text-xs text-secondary text-nowrap">{{ formatFechaHora(item.reputacion?.ultima_calificacion) }}</td>
                                    <td class="text-end">
                                        <span class="table-action-link"><i class="fas fa-chevron-right"></i></span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Tabla agentes -->
                    <div v-else class="table-responsive" :class="{ 'is-refreshing': loadingTabla }">
                        <table class="table align-items-center mb-0 rep-tabla">
                            <thead>
                                <tr>
                                    <th class="col-rank">#</th>
                                    <th>Agente</th>
                                    <th v-if="vistaGlobal">Inmobiliaria</th>
                                    <th title="Promedio de Tiempo de respuesta y Ética">Prom. agente</th>
                                    <th class="text-center">Prom. general</th>
                                    <th class="text-center">Inmuebles</th>
                                    <th class="text-center">Calif.</th>
                                    <th title="Fotos · Información · Disponibilidad · Respuesta · Ética">Criterios</th>
                                    <th>Percepción</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="!agentes.items.length && !loadingTabla">
                                    <td :colspan="vistaGlobal ? 10 : 9">
                                        <div class="empty-state empty-state--sm">
                                            <div class="empty-state__icon"><i class="fas fa-user-tie"></i></div>
                                            <p class="empty-state__title">No hay agentes con estos filtros</p>
                                            <p class="empty-state__text">Cambia el estado a "Todos" para ver agentes sin calificaciones.</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-for="(item, i) in agentes.items" :key="item.agente?.id || `sin-agente-${i}`"
                                    :class="{ 'fila-click': item.agente }" :tabindex="item.agente ? 0 : -1"
                                    :title="item.agente ? 'Ver los inmuebles de este agente' : ''"
                                    @click="verInmueblesDeAgente(item.agente)" @keyup.enter="verInmueblesDeAgente(item.agente)">
                                    <td class="col-rank">{{ posicion(i) }}</td>
                                    <td>
                                        <div class="agente-celda">
                                            <span class="avatar">{{ iniciales(item.agente?.nombre) }}</span>
                                            <div>
                                                <p class="celda-titulo">{{ item.agente?.nombre || 'Sin agente' }}</p>
                                                <p class="celda-sub">{{ item.agente?.email }}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td v-if="vistaGlobal">
                                        <p class="celda-titulo">{{ item.inmobiliaria?.nombre || '—' }}</p>
                                        <p class="celda-sub">{{ item.inmobiliaria?.email }}</p>
                                    </td>
                                    <td>
                                        <div v-if="item.promedio_agente !== null && item.promedio_agente !== undefined" class="calif-celda">
                                            <span class="calif-celda__valor">{{ formatPromedio(item.promedio_agente) }}</span>
                                            <div>
                                                <estrellas :valor="item.promedio_agente" />
                                                <span class="nivel-chip nivel-chip--xs" :style="nivelStyle(item.nivel_agente)">
                                                    {{ nivelNombre(item.nivel_agente) }}
                                                </span>
                                            </div>
                                        </div>
                                        <span v-else class="nivel-chip nivel-chip--xs" :style="nivelStyle(null)">Sin calificar</span>
                                    </td>
                                    <td class="text-center fw-semibold">{{ formatPromedio(item.promedio_general) }}</td>
                                    <td class="text-center">
                                        {{ formatNumber(item.inmuebles_calificados) }}
                                        <span class="text-secondary text-xs">/ {{ formatNumber(item.inmuebles_total) }}</span>
                                    </td>
                                    <td class="text-center fw-semibold">{{ formatNumber(item.total_calificaciones) }}</td>
                                    <td>
                                        <mini-criterios :criterios="item.criterios" :nombres="nombresCriterios" />
                                    </td>
                                    <td style="min-width:110px">
                                        <template v-if="item.total_calificaciones">
                                            <div class="sentimiento-bar sentimiento-bar--sm">
                                                <div class="sentimiento-bar__pos" :style="{ width: (item.porcentaje_positivas || 0) + '%' }"></div>
                                                <div class="sentimiento-bar__neu" :style="{ width: (item.porcentaje_neutras || 0) + '%' }"></div>
                                                <div class="sentimiento-bar__neg" :style="{ width: (item.porcentaje_negativas || 0) + '%' }"></div>
                                            </div>
                                            <span class="text-xxs text-secondary">{{ formatPorcentaje(item.porcentaje_positivas) }} positivas</span>
                                        </template>
                                        <span v-else class="text-xs text-secondary">—</span>
                                    </td>
                                    <td class="text-end">
                                        <span v-if="item.agente" class="table-action-link"><i class="fas fa-chevron-right"></i></span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="paginacion.ultima_pagina > 1" class="tabla-footer">
                        <span class="text-xs text-secondary">
                            {{ rangoFilas }} de {{ formatNumber(paginacion.total) }}
                        </span>
                        <base-pagination :current-page="paginacion.pagina" :pageCount="paginacion.ultima_pagina"
                            @nextPage="cambiarPagina('next')" @previousPage="cambiarPagina('previous')"
                            @loadPage="cambiarPagina" />
                    </div>
                </div>
            </div>

            <!-- Detalle -->
            <soft-modal @close="cerrarDetalle" :modalActive="detalleAbierto" v-if="detalleAbierto" size="xl">
                <template #header>
                    <h5 class="modal-title mb-0">
                        <i class="fas fa-star-half-alt me-2 text-warning"></i>
                        Reputación del inmueble
                        <small class="text-secondary fw-normal ms-1">· {{ periodoTexto }}</small>
                    </h5>
                </template>
                <detalle-inmueble :detalle="detalle" :loading="loadingDetalle" />
            </soft-modal>
        </template>
    </div>
</template>

<script>
import { computed, ref } from "vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftAlert from "@/components/SoftAlert.vue";
import SoftModal from "@/components/SoftModal.vue";
import CrmPageHeader from "@/components/CrmPageHeader.vue";
import BasePagination from "@/components/pagination/BasePagination.vue";
import ReputacionChart from "./components/reputacionChart.vue";
import Estrellas from "./components/estrellas.vue";
import MiniCriterios from "./components/miniCriterios.vue";
import DetalleInmueble from "./components/detalleInmueble.vue";
import { useReputacionInmuebles } from "./useReputacionInmuebles.js";
import {
    nivelStyle, nivelSolid, nivelNombre, colorPorPromedio,
    formatPromedio, formatNumber, formatPorcentaje, formatVariacion, variacionClass, variacionIcon,
    formatFechaHora, formatFecha,
} from "./reputacionHelpers.js";

export default {
    name: "ReputacionInmuebles",
    components: {
        SoftButton, SoftAlert, SoftModal, CrmPageHeader, BasePagination,
        ReputacionChart, Estrellas, MiniCriterios, DetalleInmueble,
    },
    setup() {
        const rep = useReputacionInmuebles();
        const {
            resumen, tendencia, opciones, tab, filtrosActivos, filtros,
            tablaInmuebles, tablaAgentes, inmuebles, agentes, buscarTabla,
        } = rep;

        rep.iniciar();

        const rangos = [
            { key: 'mes', label: 'Este mes' },
            { key: 'mesAnterior', label: 'Mes anterior' },
            { key: '30dias', label: '30 días' },
            { key: '90dias', label: '90 días' },
            { key: 'anio', label: 'Este año' },
            { key: 'todo', label: 'Todo' },
        ];

        const agrupaciones = [
            { key: 'dia', label: 'Día' },
            { key: 'semana', label: 'Semana' },
            { key: 'mes', label: 'Mes' },
        ];

        const ordenes = computed(() => [
            { key: 'mejor', label: tab.value === 'agentes' ? 'Mejor agente primero' : 'Mejor calificados primero' },
            { key: 'peor', label: tab.value === 'agentes' ? 'Peor agente primero' : 'Peor calificados primero' },
            { key: 'mas_calificados', label: 'Más calificaciones' },
            { key: 'recientes', label: 'Calificados recientemente' },
        ]);

        // El panel de filtros arranca abierto si llegan filtros por la URL
        const masFiltros = ref(filtrosActivos.value.some((c) => c.campo !== 'inmobiliaria_id'));
        const masTabla = ref(false);
        const filtrosSecundarios = computed(() =>
            ['agent_id', 'ciudad_id', 'tipo_inmueble', 'tipo_negocio'].filter((c) => filtros.value[c]).length
        );

        const kpis = computed(() => resumen.value?.kpis || {});
        const anterior = computed(() => resumen.value?.periodo_anterior || null);

        const destacadosVista = ref('mejores');
        const destacados = computed(() => resumen.value?.destacados?.[destacadosVista.value] || []);

        const tablaActual = computed(() => (tab.value === 'agentes' ? tablaAgentes.value : tablaInmuebles.value));
        const paginacion = computed(() => (tab.value === 'agentes' ? agentes.value : inmuebles.value).paginacion);

        const filtrosTablaActivos = computed(() => {
            const t = tablaActual.value;
            return ['min_calificaciones', 'promedio_min', 'promedio_max'].filter((c) => t[c] !== '' && t[c] !== undefined).length;
        });

        const posicion = (i) => (paginacion.value.pagina - 1) * paginacion.value.por_pagina + i + 1;
        const rangoFilas = computed(() => {
            const p = paginacion.value;
            const desde = (p.pagina - 1) * p.por_pagina + 1;
            return `${formatNumber(desde)}–${formatNumber(Math.min(p.pagina * p.por_pagina, p.total))}`;
        });

        const iniciales = (nombre) => {
            if (!nombre) return '?';
            return nombre.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
        };

        // Atajo desde la tarjeta de cobertura
        const verSinCalificar = async () => {
            tablaInmuebles.value.estado = 'sin_calificar';
            document.querySelector('.tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (tab.value !== 'inmuebles') await rep.cambiarTab('inmuebles');
            else await buscarTabla();
        };

        const nombresCriterios = computed(() => {
            const map = {};
            (opciones.value.criterios || []).forEach((c) => { map[c.clave] = c.nombre; });
            return map;
        });

        // --- Gráficas ----------------------------------------------------
        const fuente = { family: 'Inter, Open Sans, sans-serif', size: 11 };

        const tendenciaTieneDatos = computed(() =>
            (tendencia.value?.puntos || []).some((p) => Number(p.total) > 0)
        );

        const tendenciaData = computed(() => {
            const puntos = tendencia.value?.puntos || [];
            return {
                labels: puntos.map((p) => p.etiqueta),
                datasets: [
                    {
                        type: 'line',
                        label: 'Promedio general',
                        data: puntos.map((p) => p.promedio_general),
                        yAxisID: 'y1',
                        borderColor: '#F59E0B',
                        backgroundColor: '#F59E0B',
                        pointBackgroundColor: puntos.map((p) => colorPorPromedio(p.promedio_general)),
                        pointBorderColor: '#fff',
                        pointBorderWidth: 1.5,
                        borderWidth: 2.5,
                        tension: 0.35,
                        pointRadius: puntos.length > 60 ? 0 : 4,
                        pointHoverRadius: 6,
                        spanGaps: false,
                        order: 0,
                    },
                    {
                        type: 'bar',
                        label: 'Calificaciones',
                        data: puntos.map((p) => p.total),
                        yAxisID: 'y',
                        backgroundColor: 'rgba(37, 99, 235, 0.18)',
                        hoverBackgroundColor: 'rgba(37, 99, 235, 0.35)',
                        borderRadius: 4,
                        maxBarThickness: 32,
                        order: 1,
                    },
                ],
            };
        });

        const tendenciaOptions = {
            interaction: { intersect: false, mode: 'index' },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: '#1E293B',
                    padding: 10,
                    titleFont: { ...fuente, weight: '600' },
                    bodyFont: fuente,
                    callbacks: {
                        label: (ctx) => {
                            if (ctx.dataset.type === 'line') {
                                return ctx.raw === null ? ' Sin calificaciones' : ` Promedio: ${Number(ctx.raw).toFixed(1)} ★`;
                            }
                            return ` Calificaciones: ${formatNumber(ctx.raw)}`;
                        },
                        afterBody: (items) => {
                            const p = (tendencia.value?.puntos || [])[items[0]?.dataIndex];
                            if (!p || !p.total) return '';
                            return `Positivas: ${p.positivas} · Negativas: ${p.negativas}`;
                        },
                    },
                },
            },
            scales: {
                x: { grid: { display: false }, ticks: { font: fuente, color: '#94A3B8', maxRotation: 0, autoSkipPadding: 12 } },
                y: {
                    beginAtZero: true,
                    position: 'left',
                    ticks: { precision: 0, font: fuente, color: '#94A3B8' },
                    grid: { borderDash: [4, 4], drawBorder: false, color: '#EEF2F7' },
                },
                y1: {
                    min: 1,
                    max: 5,
                    position: 'right',
                    ticks: { stepSize: 1, font: fuente, color: '#D97706', callback: (v) => `${v}★` },
                    grid: { drawOnChartArea: false, drawBorder: false },
                },
            },
        };

        const nivelesData = computed(() => {
            const niveles = resumen.value?.niveles || [];
            return {
                labels: niveles.map((n) => n.nombre),
                datasets: [{
                    data: niveles.map((n) => n.inmuebles),
                    backgroundColor: niveles.map((n) => nivelSolid(n.clave)),
                    borderWidth: 3,
                    borderColor: '#fff',
                    hoverOffset: 4,
                }],
            };
        });

        const nivelesOptions = {
            cutout: '72%',
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: '#1E293B',
                    callbacks: {
                        label: (ctx) => {
                            const nivel = (resumen.value?.niveles || [])[ctx.dataIndex];
                            return ` ${ctx.label}: ${ctx.parsed} inmuebles (${formatPorcentaje(nivel?.porcentaje)})`;
                        },
                    },
                },
            },
        };

        return {
            ...rep,
            rangos, agrupaciones, ordenes, masFiltros, masTabla, filtrosSecundarios,
            kpis, anterior, destacadosVista, destacados,
            tablaActual, paginacion, filtrosTablaActivos, posicion, rangoFilas, iniciales, verSinCalificar,
            nombresCriterios,
            tendenciaTieneDatos, tendenciaData, tendenciaOptions, nivelesData, nivelesOptions,
            nivelStyle, nivelSolid, nivelNombre, colorPorPromedio,
            formatPromedio, formatNumber, formatPorcentaje, formatVariacion, variacionClass, variacionIcon,
            formatFechaHora, formatFecha,
        };
    },
};
</script>

<style lang="scss" scoped>
$text: #1E293B;
$muted: #64748B;
$soft: #94A3B8;
$border: #E2E8F0;
$line: #F1F5F9;
$primary: #2563EB;

// --- Botones / controles genéricos ---------------------------------------
.icon-btn {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid $border;
    background: #fff;
    color: $muted;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        color: $primary;
        border-color: #BFDBFE;
        background: #EFF6FF;
    }
}

.segmented {
    display: inline-flex;
    flex-wrap: wrap;
    background: #F1F5F9;
    border-radius: 9px;
    padding: 3px;
    gap: 2px;

    &__btn {
        border: none;
        background: transparent;
        color: $muted;
        font-size: 0.75rem;
        font-weight: 500;
        padding: 5px 12px;
        border-radius: 7px;
        white-space: nowrap;
        transition: all 0.15s ease;

        &:hover:not(:disabled):not(.is-active) {
            color: $text;
        }

        &.is-active {
            background: #fff;
            color: $text;
            font-weight: 600;
            box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
        }

        &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
    }

}

.segmented--sm .segmented__btn {
    font-size: 0.7rem;
    padding: 4px 10px;
}

// --- Filtros ---------------------------------------------------------------
.filtros-card {
    overflow: visible;
}

.filtros-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;

    &__right {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left: auto;
    }
}

.fechas {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid $border;
    border-radius: 9px;
    padding: 3px 10px;
    color: $soft;
    font-size: 0.75rem;
    background: #fff;
    transition: border-color 0.15s ease;

    &.is-active {
        border-color: $primary;
        color: $primary;
    }

    input {
        border: none;
        outline: none;
        font-size: 0.75rem;
        color: $text;
        background: transparent;
        padding: 2px 0;
        max-width: 125px;
    }
}

.filtros-toggle {
    display: inline-flex;
    align-items: center;
    border: 1px solid $border;
    background: #fff;
    border-radius: 9px;
    padding: 6px 12px;
    font-size: 0.75rem;
    font-weight: 600;
    color: $muted;
    white-space: nowrap;
    transition: all 0.15s ease;

    &:hover,
    &.is-open {
        border-color: #BFDBFE;
        color: $primary;
        background: #EFF6FF;
    }

    &__count {
        margin-left: 6px;
        background: $primary;
        color: #fff;
        border-radius: 999px;
        font-size: 0.62rem;
        min-width: 17px;
        height: 17px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0 5px;
    }

    &__chevron {
        font-size: 0.6rem;
        transition: transform 0.2s ease;
    }

    &--sm {
        padding: 5px 10px;
    }
}

.filtros-toggle.is-open .filtros-toggle__chevron {
    transform: rotate(180deg);
}

.filtros-panel {
    border-top: 1px solid $line;
    padding: 14px 16px;
    background: #FBFCFE;
}

.filter-label {
    font-size: 0.68rem;
    font-weight: 600;
    color: $muted;
    margin-bottom: 4px;
    display: block;
}

.chips-activos {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    border-top: 1px solid $line;
    padding: 10px 16px;
}

.chip-filtro {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #EFF6FF;
    color: #1E40AF;
    border-radius: 999px;
    padding: 3px 4px 3px 10px;
    font-size: 0.7rem;

    &__label {
        color: #60A5FA;
    }

    button {
        border: none;
        background: transparent;
        color: #60A5FA;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        font-size: 0.6rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        &:hover {
            background: #DBEAFE;
            color: #1E40AF;
        }
    }
}

.link-limpiar {
    border: none;
    background: transparent;
    font-size: 0.7rem;
    color: $muted;
    padding: 3px 6px;

    &:hover {
        color: #DC2626;
    }
}

.rep-select {
    min-width: 0;

    &--inmobiliaria {
        width: 260px;
    }

    :deep(.vs__dropdown-toggle) {
        border: 1px solid #d2d6da;
        border-radius: 9px;
        padding: 0;
        min-height: 33px;
        background: #fff;
    }

    :deep(.vs__search),
    :deep(.vs__selected),
    :deep(.vs__dropdown-menu) {
        font-size: 0.78rem;
        color: #495057;
    }

    :deep(.vs__selected) {
        margin: 2px 2px 2px 6px;
    }
}

// --- Estados de carga ------------------------------------------------------
.progress-line {
    height: 2px;
    margin: -14px 0 12px;
    border-radius: 2px;
    overflow: hidden;
    position: relative;
    visibility: hidden;

    &.is-active {
        visibility: visible;

        &::after {
            content: '';
            position: absolute;
            top: 0;
            left: -40%;
            width: 40%;
            height: 100%;
            background: $primary;
            animation: progreso 1s ease-in-out infinite;
        }
    }

    &--tabla {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        margin: 0;
        z-index: 2;
    }
}

@keyframes progreso {
    from { left: -40%; }
    to { left: 100%; }
}

.is-refreshing {
    opacity: 0.55;
    transition: opacity 0.2s ease;
    pointer-events: none;
}

.skeleton {
    border-radius: 8px;
    background: linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 37%, #F1F5F9 63%);
    background-size: 400% 100%;
    animation: shimmer 1.4s ease infinite;

    &--kpi { height: 112px; }
    &--chart { height: 260px; }
}

@keyframes shimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
}

// --- Estado vacío ----------------------------------------------------------
.empty-state {
    text-align: center;
    padding: 48px 16px;

    &__icon {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #F1F5F9;
        color: $soft;
        font-size: 1.4rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 12px;
    }

    &__title {
        font-size: 0.9rem;
        font-weight: 600;
        color: $text;
        margin: 0 0 4px;
    }

    &__text {
        font-size: 0.78rem;
        color: $muted;
        margin: 0 auto 16px;
        max-width: 420px;
    }

    &--sm {
        padding: 28px 16px;

        .empty-state__icon {
            width: 44px;
            height: 44px;
            font-size: 1.1rem;
        }
    }
}

.empty-text {
    font-size: 0.75rem;
    color: $soft;
    text-align: center;
    padding: 32px 0;
    margin: 0;
}

// --- KPIs ------------------------------------------------------------------
.kpi {
    display: flex;
    flex-direction: column;
    padding: 18px;

    &__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
    }

    &__info {
        color: #CBD5E1;
        font-size: 0.75rem;
        cursor: help;

        &:hover {
            color: $muted;
        }
    }

    &__value {
        font-size: 1.75rem;
        font-weight: 700;
        color: $text;
        line-height: 1;
    }

    &__of {
        font-size: 0.75rem;
        color: $soft;
        padding-bottom: 2px;
    }

    &__hint {
        font-size: 0.7rem;
        color: $soft;
        margin: 6px 0 0;
    }

    &__split {
        display: flex;
        gap: 8px;
        margin-top: 12px;

        > div {
            flex: 1;
            background: #F8FAFC;
            border-radius: 8px;
            padding: 6px 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        span {
            font-size: 0.68rem;
            color: $muted;
        }

        b {
            font-size: 0.82rem;
            color: $text;
        }
    }

    &__deltas {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 10px;
    }

    &__link {
        font-size: 0.72rem;
        font-weight: 600;
        color: $primary;
        text-decoration: none;
        padding-top: 8px;

        &:hover {
            text-decoration: underline;
        }
    }
}

.delta {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    border-radius: 999px;
    padding: 2px 8px;
    font-size: 0.7rem;
    font-weight: 600;
    cursor: help;

    small {
        font-weight: 400;
        opacity: 0.8;
        margin-left: 2px;
    }

    &.var-sube { background: #ECFDF5; color: #047857; }
    &.var-baja { background: #FEF2F2; color: #B91C1C; }
    &.var-neutra { background: #F1F5F9; color: $muted; }
}

.nivel-chip {
    display: inline-block;
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 0.68rem;
    font-weight: 600;
    white-space: nowrap;

    &--xs {
        font-size: 0.62rem;
        padding: 1px 8px;
        margin-top: 2px;
    }
}

.sentimiento-bar {
    display: flex;
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
    background: #F1F5F9;
    margin-top: 12px;
    gap: 2px;

    &__pos { background: #10B981; }
    &__neu { background: #F59E0B; }
    &__neg { background: #EF4444; }

    &--sm {
        height: 6px;
        margin: 0 0 3px;
    }
}

.leyenda {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin-top: 10px;
    font-size: 0.68rem;
    color: $muted;
}

.dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 5px;

    &--pos { background: #10B981; }
    &--neu { background: #F59E0B; }
    &--neg { background: #EF4444; }
}

.bar {
    height: 6px;
    border-radius: 999px;
    background: #F1F5F9;
    overflow: hidden;

    &__fill {
        height: 100%;
        border-radius: 999px;
        transition: width 0.4s ease;
    }
}

// --- Tarjetas de gráficas --------------------------------------------------
.card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 18px 0;

    &--tabla {
        padding: 14px 18px;
        border-bottom: 1px solid $line;
    }
}

.card-content {
    padding: 14px 18px 18px;
}

.card-title-sm {
    font-size: 0.85rem;
    font-weight: 600;
    color: $text;
    margin: 0;
}

.card-hint {
    font-size: 0.7rem;
    color: $soft;
    margin: 2px 0 0;
    display: flex;
    align-items: center;
}

.legend-swatch {
    display: inline-block;
    margin-right: 5px;

    &--bar {
        width: 10px;
        height: 10px;
        border-radius: 2px;
        background: rgba(37, 99, 235, 0.3);
    }

    &--line {
        width: 14px;
        height: 3px;
        border-radius: 2px;
        background: #F59E0B;
    }
}

.crit-row {
    position: relative;
    padding: 8px 10px;
    margin: 0 -10px;
    border-radius: 8px;

    &--top { background: #F0FDF4; }
    &--low { background: #FFFBEB; }

    &__name {
        font-size: 0.76rem;
        color: #334155;
    }

    &__icon {
        color: #CBD5E1;
        font-size: 0.65rem;
        width: 14px;
    }

    &__val {
        font-size: 0.82rem;
        font-weight: 700;
        color: $text;
    }

    .bar {
        margin-top: 5px;
    }
}

.crit-tag {
    display: inline-block;
    margin-top: 5px;
    font-size: 0.62rem;
    font-weight: 600;

    &--ok { color: #047857; }
    &--warn { color: #B45309; }
}

.dist-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;

    &__label {
        font-size: 0.76rem;
        font-weight: 600;
        color: #334155;
        width: 32px;

        i {
            color: #F59E0B;
            font-size: 0.62rem;
        }
    }

    .bar {
        height: 10px;
    }

    &__pct {
        font-size: 0.72rem;
        color: $muted;
        width: 46px;
        text-align: right;
    }
}

.dona {
    position: relative;

    &__centro {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        pointer-events: none;

        b {
            font-size: 1.4rem;
            color: $text;
            line-height: 1;
        }

        span {
            font-size: 0.66rem;
            color: $soft;
        }
    }
}

.niveles-leyenda {
    margin-top: 12px;

    &__item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.74rem;
        color: #334155;
        padding: 4px 0;

        b {
            color: $text;
        }
    }

    &__pct {
        width: 46px;
        text-align: right;
        color: $soft;
        font-size: 0.7rem;
    }
}

.destacado {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    margin: 0 -8px;
    border-radius: 8px;
    text-decoration: none;
    transition: background 0.15s ease;

    &:hover {
        background: #F8FAFC;
    }

    &__body {
        flex: 1;
        min-width: 0;
    }

    &__titulo {
        font-size: 0.78rem;
        font-weight: 600;
        color: $text;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__sub {
        font-size: 0.66rem;
        color: $soft;
        margin: 0;
    }
}

.rank {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    font-size: 0.68rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--mejores { background: #ECFDF5; color: #047857; }
    &--peores { background: #FEF2F2; color: #B91C1C; }
}

// --- Tabla -----------------------------------------------------------------
.tabs {
    display: inline-flex;
    gap: 4px;

    &__btn {
        border: none;
        background: transparent;
        font-size: 0.8rem;
        font-weight: 600;
        color: $muted;
        padding: 7px 14px;
        border-radius: 8px;
        transition: all 0.15s ease;

        &:hover:not(.is-active) {
            background: #F1F5F9;
            color: $text;
        }

        &.is-active {
            background: $text;
            color: #fff;
        }
    }
}

.total-badge {
    font-size: 0.72rem;
    color: $muted;
    background: #F1F5F9;
    border-radius: 999px;
    padding: 4px 12px;
}

.tabla-controles {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 12px 18px;

    .control {
        width: auto;
        min-width: 150px;
        border-radius: 9px !important;
        font-size: 0.75rem !important;
    }

    &--extra {
        padding-top: 0;
        align-items: flex-end;
    }
}

.buscador {
    flex: 1 1 260px;
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #d2d6da;
    border-radius: 9px;
    padding: 0 10px;
    background: #fff;
    min-height: 33px;

    &:focus-within {
        border-color: #93C5FD;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }

    > i {
        color: $soft;
        font-size: 0.75rem;
    }

    input {
        flex: 1;
        border: none;
        outline: none;
        font-size: 0.78rem;
        color: $text;
        background: transparent;
        min-width: 0;
    }

    button {
        border: none;
        background: transparent;
        color: $soft;
        font-size: 0.7rem;

        &:hover {
            color: $text;
        }
    }
}

.mini-field {
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin: 0;

    span {
        font-size: 0.66rem;
        font-weight: 600;
        color: $muted;
    }

    input {
        width: 130px;
        border-radius: 9px !important;
    }
}

.rep-tabla {
    .col-rank {
        width: 44px;
        color: $soft;
        font-weight: 600;
        font-size: 0.72rem !important;
        text-align: center;
    }
}

.fila-click {
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover > td,
    &:focus > td {
        background: #F8FAFF !important;
    }

    &:focus {
        outline: none;
    }

    &:hover .table-action-link {
        color: $primary;
    }
}

.celda-titulo {
    font-size: 0.8rem;
    font-weight: 600;
    color: $text;
    margin: 0;
    max-width: 260px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.celda-sub {
    font-size: 0.68rem;
    color: $soft;
    margin: 0;
    white-space: nowrap;
}

.codigo {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    color: $muted;
}

.agente-celda {
    display: flex;
    align-items: center;
    gap: 8px;
}

.avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #E0E7FF;
    color: #4338CA;
    font-size: 0.65rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.calif-celda {
    display: flex;
    align-items: center;
    gap: 8px;

    &__valor {
        font-size: 1.1rem;
        font-weight: 700;
        color: $text;
        min-width: 28px;
    }

    > div {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
}

.tabla-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 18px;
    border-top: 1px solid $line;
}

@media (max-width: 768px) {
    .filtros-bar__right {
        margin-left: 0;
        width: 100%;
    }

    .rep-select--inmobiliaria {
        flex: 1;
        width: auto;
    }

    .segmented {
        width: 100%;
    }

    .segmented__btn {
        flex: 1;
    }

    .tabla-controles .control {
        flex: 1;
    }
}
</style>
